/**
 * Enveloppe une zone de saisie appartenant à un mois clôturé.
 *
 * Plutôt que de propager un `disabled` dans la dizaine de champs d'une ligne
 * de tracker, on neutralise le bloc entier avec `inert` : l'attribut natif
 * coupe le clic ET le clavier ET le focus, ce qu'un `pointer-events: none`
 * ne fait pas (la ligne resterait atteignable à la tabulation).
 *
 * Purement cosmétique : l'autorité reste kronos-api. Si ce composant se
 * trompe, le serveur refuse quand même.
 */
import type { ReactNode } from 'react';
import { Box, Tooltip, type SxProps, type Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Le mois de cette saisie est clôturé pour l'utilisateur courant. */
  closed: boolean;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

export default function TimeLockGuard({ closed, children, sx }: Props) {
  const { t } = useTranslation();
  if (!closed) return <>{children}</>;
  return (
    // L'infobulle vit SUR le parent : un enfant `inert` n'émet plus de survol.
    <Tooltip title={t('kronos.timeLock.tooltip')}>
      <Box sx={{ cursor: 'not-allowed', ...sx }} data-testid="time-lock-guard">
        <Box inert style={{ opacity: 0.5 }}>{children}</Box>
      </Box>
    </Tooltip>
  );
}
